import { Box, ControlBox, Flex, VisuallyHidden } from "@chakra-ui/core";
import FieldError from "atoms/FieldError";
import Label from "atoms/Label";
import { PSmall } from "atoms/legacy-typography";
import { useField } from "formik";
import { colors } from "lib/theme";
import React from "react";
import styled from "styled-components";
import Icon from "./check.svg";

// The hidden input element that powers the style interaction
const HiddenInput = styled.input`
  appearance: none;
  width: 0;
  height: 0;
  border: none;
  opacity: 0.00001;
`;

HiddenInput.defaultProps = {
  type: "checkbox",
};

// The element that looks like a real input
const FakeInput = styled.div`
  min-width: 30px;
  height: 30px;

  border: 2px solid ${colors.gray[500]};
  border-radius: 4px;
  cursor: pointer;
  background-color: ${colors.white};

  ${HiddenInput}:focus ~ &,
  ${HiddenInput}:checked ~ &,
  :hover {
    border-color: ${colors.pink[500]};
  }

  ${HiddenInput}:focus ~ & {
    border-width: 2px;
  }

  ${(p) => p.error && `border-color: ${colors.red[500]};`}
`;

// The element that looks like a label
const LegacyCheckboxLabel = styled(PSmall)`
  color: ${colors.navy[500]};
  margin-left: 10px;
  margin-top: 3px;
  cursor: pointer;
  ${(p) => p.labelSize === "lg" && `font-size: 16px;`}
`;

export const UnstyledCheck = Icon;
const IconStyled = styled(Icon)`
  display: none;
  flex-shrink: 0;

  ${HiddenInput}:checked ~ & {
    display: inline;
  }

  cursor: pointer;
  width: 30px;
  margin-left: -30px;
`;

const LegacyCheckbox = ({
  label,
  error,
  hidden,
  fixedWidth,
  name,
  labelSize = "sm",
  onClick,
  ...props
}) => (
  <Flex as="label" alignItems="flex-start" mb="8px" onClick={onClick}>
    {hidden && <HiddenInput hidden {...props} name={name} />}
    {!hidden && (
      <>
        <Flex alignItems="center">
          <HiddenInput {...props} name={name} />
          <FakeInput error={error} />
          <IconStyled />
        </Flex>
        <LegacyCheckboxLabel labelSize={labelSize}>{label}</LegacyCheckboxLabel>
      </>
    )}
  </Flex>
);

// Wrapper to receive formik field props
export const LegacyCheckboxField = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldTouched }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  ...props
}) => {
  return (
    <LegacyCheckbox
      {...field}
      {...props}
      error={touched[field.name] && errors[field.name]}
      onClick={() => setFieldTouched(field.name, true)}
    />
  );
};

const stylesProps = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  rounded: "4px",
  width: 4,
  height: 4,
  border: "2px solid",
  borderColor: "navy.500",
  bg: "gray.50",
  mr: 2,
  _checkedAndChild: {
    opacity: 1,
    transform: `scale(1)`,
  },
  _hover: {
    bg: "navy.100",
  },
  _checkedAndHover: {
    cursor: "pointer",
  },
  _disabled: {
    cursor: "not-allowed",
    borderColor: "gray.500",
  },
};

export const CheckboxGroup = ({ label, name, disabled = false, ...props }) => {
  const [field, meta, helpers] = useField({ name });
  return (
    <Box {...props}>
      <Flex
        as="label"
        cursor="pointer"
        mb={1}
        onClick={() => helpers.setTouched(true)}
      >
        <VisuallyHidden
          as="input"
          type="checkbox"
          {...field}
          disabled={disabled}
          checked={field.value}
        />
        <ControlBox {...stylesProps}>
          <Box
            h="10px"
            w="10px"
            rounded="2px"
            transform="scale(0)"
            opacity={0}
            transition="0.05s ease-in-out transform, 0.05s ease-in-out opacity"
            bg="navy.500"
          />
        </ControlBox>
        <Label as="p" mt="1px" pb={0} {...(disabled && { color: "gray.500" })}>
          {label}
        </Label>
      </Flex>
      <Flex minHeight={[6, , 10]} py={[1, , 2]} alignItems="center">
        <FieldError opacity={meta.touched && meta.error ? 1 : 0}>
          {meta.error}
        </FieldError>
      </Flex>
    </Box>
  );
};
